import { setOpenPopupBoardIds } from 'modules/overlay-v2/store/overlay/overlay';
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import { useAppDispatch } from 'store/hooks';

/** `null` — основное окно оверлея, число — окно конкретного табло. */
export type PopupBoardId = number | null;

export interface PopupRegistryValue {
  /** Запомнить окно, открытое панелью. `null` — браузер заблокировал открытие. */
  register: (boardId: PopupBoardId, popup: Window | null) => void;
  /** Разослать команду во все живые окна: с одной панели открывают и оверлей, и экран в зале. */
  send: (message: object) => void;
}

const PopupRegistryContext = createContext<PopupRegistryValue | null>(null);

/**
 * Живые ссылки на открытые окна оверлея.
 *
 * Окно — это ресурс, а не состояние приложения: его нельзя сериализовать, сравнить
 * по значению или восстановить из снапшота. Раньше `Window` лежали прямо в Redux, и это
 * стоило трёх неприятностей: RTK ругался на несериализуемый payload при каждом «Открыть
 * окно», дедупликация держалась на том, что Immer не оборачивает хост-объекты в прокси,
 * а слайс был вынужден кастовать состояние к `any`.
 *
 * Здесь ссылки живут в ref, а в Redux уходит только сериализуемая сводка — список табло,
 * для которых окна сейчас открыты.
 */
export function PopupRegistryProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const popups = useRef(new Map<PopupBoardId, Window>());

  /** Выкинуть закрытые окна и обновить сводку в сторе. */
  const syncOpened = useCallback(() => {
    const openBoardIds: PopupBoardId[] = [];
    popups.current.forEach((popup, boardId) => {
      if (popup.closed) popups.current.delete(boardId);
      else openBoardIds.push(boardId);
    });
    dispatch(setOpenPopupBoardIds(openBoardIds));
  }, [dispatch]);

  const register = useCallback(
    (boardId: PopupBoardId, popup: Window | null) => {
      if (!popup) return;
      // Ключ — табло: повторное «Открыть окно» переоткрывает то же окно, а не плодит новые.
      popups.current.set(boardId, popup);
      syncOpened();
    },
    [syncOpened],
  );

  const send = useCallback(
    (message: object) => {
      const payload = JSON.stringify(message);
      let hasClosed = false;

      popups.current.forEach((popup, boardId) => {
        if (popup.closed) {
          popups.current.delete(boardId);
          hasClosed = true;
          return;
        }
        popup.postMessage(payload, window.location.origin);
      });

      if (hasClosed) syncOpened();
    },
    [syncOpened],
  );

  const value = useMemo(() => ({ register, send }), [register, send]);

  return <PopupRegistryContext.Provider value={value}>{children}</PopupRegistryContext.Provider>;
}

export function usePopupRegistry(): PopupRegistryValue {
  const value = useContext(PopupRegistryContext);
  // eslint-disable-next-line lingui/no-unlocalized-strings -- текст Error для разработчика
  if (!value) throw new Error('usePopupRegistry вызван вне PopupRegistryProvider');
  return value;
}
